import React, { useEffect, useState } from 'react';
import './Footer.scss';
import { NavLink, useLocation } from 'react-router-dom';
import SvgIcons from '../../services/SvgIcons';
import Legacy from '../../components/Legacy/Legacy';
import useContactForm from '../../hooks/useContactForm';
import { getContent } from '../../services/strapi/endpoints';
import { isExternalLink, safeHtml } from '../../services/helpers';
import { baseStrapiURL } from '../../services/config';

const Footer = ({setNotification, setLegacy, setShowLegacy}) => {
    const {
        values,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit
    } = useContactForm({ setNotification });

    const location = useLocation();

    const handleClick = (e, to) => {
        // Si nous sommes déjà sur la page concernée
        if (location.pathname === to) {
            e.preventDefault(); // Empêche la navigation
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    // Chargement du script reCAPTCHA v3
    useEffect(() => {
        const loadRecaptcha = () => {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
            script.async = true;
            document.head.appendChild(script);
        };
        loadRecaptcha();

        return () => {
            // Nettoyage si nécessaire
            const script = document.querySelector(`script[src*="recaptcha"]`);
            if (script) script.remove();
        };
    }, []);

    const strapiURl = {
        page: 'footer',
        components: [
            '[Logo][populate]=*&',
            '[LienReseauSocial][populate]=*&',
            '[LienNavigation][populate]=*&',
            '[Contact][populate]=*&',
            '[Rdv][populate]=*',
        ],
    }

    const [strapiData, setStrapiData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getContent(strapiURl);
                setStrapiData(response.data.data);
                setLegacy(response.data.data.MentionsLégales);

                setIsLoading(false);
            } catch (err) {
                setError(err);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [])

    if (isLoading) return <div>Chargement...</div>;
    if (error) return <div>Une erreur est survenue</div>;
    if (!strapiData) return <div>Aucune donnée trouvée</div>;

    return (
        <footer className="footer">
            <div className="footer__container">
                <div className="line line1">
                    <div className="footer__logos">
                        <div className="footer__logoSocial">
                            <NavLink
                                to="/"
                                className="footer__logo_link"
                            >
                                <img
                                    src={baseStrapiURL + strapiData.Logo.url}
                                    alt={strapiData.Logo.alternativeText === null ? '' : strapiData.Logo.alternativeText}
                                    className="logo"
                                    loading="lazy"
                                />
                            </NavLink>
                            <div className="footer__social">
                                {
                                    strapiData.LienReseauSocial.map((socialItem) => (
                                        <a
                                            key={socialItem.id}
                                            href={socialItem.LienNavigation}
                                            className="footer__social__link"
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            <img src={baseStrapiURL + socialItem.Icone.url} alt={socialItem.Icone.alternativeText} className='footer__social__link__img' loading="lazy" />
                                        </a>
                                    ))
                                }
                            </div>
                        </div>
                        <div className={"footer__crenolibre"}>
                            {
                                strapiData.Rdv.Logo &&
                                <a
                                    href={strapiData.Rdv.LienLogo}
                                    className="footer__crenolibre__link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={baseStrapiURL + strapiData.Rdv.Logo.formats.small.url} alt={strapiData.Rdv.Logo.alternativeText} loading="lazy" />
                                </a>
                            }
                            {
                                strapiData.Rdv.Bouton &&
                                <a
                                    href={strapiData.Rdv.Bouton.LienBouton}
                                    className="footer__crenolibre__rdv_btn"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {strapiData.Rdv.Bouton.TexteBouton}
                                </a>
                            }
                        </div>
                    </div>
                    <div className="footer__menu_contact">
                        <div className="footer__menu">
                            {
                                strapiData.LienNavigation.map((menuItem) => (
                                    isExternalLink(menuItem.LienNavigation) ?
                                        <a
                                            key={menuItem.id}
                                            href={menuItem.LienNavigation}
                                            className="footer__link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {menuItem.TitreNavigation}
                                        </a>
                                    :
                                        <NavLink
                                            key={menuItem.id}
                                            to={menuItem.LienNavigation === null ? '/' : '/' + menuItem.LienNavigation}
                                        className="footer__link"
                                        onClick={e => handleClick(e, menuItem.LienNavigation === null ? '/' : '/' + menuItem.LienNavigation)}
                                    >
                                        {menuItem.TitreNavigation}
                                    </NavLink>
                                ))
                            }
                        </div>
                        <div className="footer__contact">
                            <p className="name">{strapiData.Contact.PrénomNom}</p>
                            <div>
                                <a href={"tel:" + strapiData.Contact.Téléphone} className="contact-link contact-link--phone">
                                <SvgIcons
                                    name={'phone'}
                                    width={25}
                                    height={25}
                                    color={'#6D4F47'}
                                    marginRight={10}
                                />
                                {strapiData.Contact.Téléphone}
                                </a>
                            </div>
                            <div>
                                <a href={"mailto:" + strapiData.Contact.Email} className="contact-link contact-link--email">
                                    <SvgIcons
                                        name={'mail'}
                                        width={25}
                                        height={25}
                                        color={'#6D4F47'}
                                        marginRight={10}
                                    />
                                    {strapiData.Contact.Email}
                                </a>
                            </div>
                            <a 
                                href="https://www.google.com/maps/search/?api=1&query=2146 route de mende 34090 Montpellier"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="contact-link contact-link--map"
                            >
                                <SvgIcons
                                    name={'location'}
                                    width={25}
                                    height={25}
                                    color={'#6D4F47'}
                                    marginRight={10}
                                />
                                <div className="" dangerouslySetInnerHTML={safeHtml(strapiData.Contact.Adresse)} />
                            </a>
                            <p>{strapiData.Contact.Note}</p>
                        </div>
                    </div>
                    <div className="footer__form">
                        <form className="footer__contact-form" onSubmit={handleSubmit}>
                            <div className="footer__form-group">
                                <input
                                    type="text"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    placeholder="Nom"
                                    className={errors.name ? 'error' : ''}
                                    disabled={isSubmitting}
                                />
                                {errors.name && (
                                    <span className="error-message">
                                        {errors.name}
                                    </span>
                                )}
                            </div>

                            <div className="footer__form-group">
                                <input
                                    type="email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    placeholder="E-mail"
                                    className={errors.email ? 'error' : ''}
                                    disabled={isSubmitting}
                                />
                                {errors.email && (
                                    <span className="error-message">
                                        {errors.email}
                                    </span>
                                )}
                            </div>

                            <div className="footer__form-group">
                                <input
                                    type="tel"
                                    name="phone"
                                    value={values.phone}
                                    onChange={handleChange}
                                    placeholder="Téléphone"
                                    className={errors.phone ? 'error' : ''}
                                    disabled={isSubmitting}
                                />
                                {errors.phone && (
                                    <span className="error-message">
                                        {errors.phone}
                                    </span>
                                )}
                            </div>

                            <div className="footer__form-group">
                                <textarea
                                    name="message"
                                    value={values.message}
                                    onChange={handleChange}
                                    placeholder="Rédigez votre message ici..."
                                    className={errors.message ? 'error' : ''}
                                    disabled={isSubmitting}
                                    rows={5}
                                />
                                {errors.message && (
                                    <span className="error-message">
                                        {errors.message}
                                    </span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="footer__submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </form>
                    </div>
                </div>
                <div className="line line2">
                    
                </div>
                <div className="line line3">
                    <NavLink to="/"
                        className="footer__copyright__link"
                        onClick={e => handleClick(e, "/")}
                    >
                        {strapiData.Copyright}
                    </NavLink>
                    <button
                        className="footer__legacy"
                        onClick={() => setShowLegacy(true)}
                    >
                            Mentions légales
                    </button>
                </div>
                {/* Mention reCAPTCHA discrète */}
                <span className="recaptcha-footer-notice">
                    Site protégé par reCAPTCHA
                </span>
            </div>
        </footer>
    );
};

export default Footer;